import { useCallback, useEffect, useMemo, useState } from "react";
import { type Identifier, useGetList, useTranslate } from "ra-core";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Search,
  Users,
  X,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import type { Contact, ContactAudienceSelection } from "../types";

const CONTACTS_PER_PAGE = 25;
const EMPTY_FILTER: Record<string, unknown> = {};

export type ContactAudienceEstimate = {
  mode: ContactAudienceSelection["mode"];
  count: number;
  total?: number;
  selectedAllMatching: boolean;
};

export function ContactAudienceSelector({
  initialContactIds,
  resetKey = 0,
  disabled = false,
  listFilter = EMPTY_FILTER,
  excludedListFilter = EMPTY_FILTER,
  selectAllMatchingByDefault = false,
  onChange,
}: {
  initialContactIds?: Identifier[];
  resetKey?: number;
  disabled?: boolean;
  listFilter?: Record<string, unknown>;
  excludedListFilter?: Record<string, unknown>;
  selectAllMatchingByDefault?: boolean;
  onChange: (
    selection: ContactAudienceSelection,
    estimate: ContactAudienceEstimate,
  ) => void;
}) {
  const translate = useTranslate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [mode, setMode] =
    useState<ContactAudienceSelection["mode"]>("explicit");
  const [selectedIds, setSelectedIds] = useState<Identifier[]>(() =>
    uniqueIdentifiers(initialContactIds ?? []),
  );
  const [excludedIds, setExcludedIds] = useState<Identifier[]>([]);
  const [pastedContactIds, setPastedContactIds] = useState("");
  const initialKey = useMemo(
    () =>
      uniqueIdentifiers(initialContactIds ?? [])
        .map(String)
        .join("\n"),
    [initialContactIds],
  );
  const searchFilter = useMemo(() => {
    const query = search.trim();
    return query ? { q: query } : {};
  }, [search]);
  const effectiveListFilter = useMemo(
    () => ({ ...listFilter, ...searchFilter }),
    [listFilter, searchFilter],
  );
  const filterModeActive = mode === "filter" || selectAllMatchingByDefault;
  const {
    data: contacts = [],
    total,
    isPending,
    pageInfo,
  } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page, perPage: CONTACTS_PER_PAGE },
      sort: { field: "last_seen", order: "DESC" },
      filter: effectiveListFilter,
    },
    { enabled: !disabled },
  );
  const hasExcludedFilter = Object.keys(excludedListFilter).length > 0;
  const excludedCountFilter = useMemo(
    () => ({ ...excludedListFilter, ...searchFilter }),
    [excludedListFilter, searchFilter],
  );
  const { total: excludedTotal } = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "last_seen", order: "DESC" },
      filter: excludedCountFilter,
    },
    { enabled: !disabled && hasExcludedFilter },
  );
  const excludedCount = hasExcludedFilter ? (excludedTotal ?? 0) : 0;
  const selectedIdSet = useMemo(
    () => new Set(selectedIds.map(String)),
    [selectedIds],
  );
  const excludedIdSet = useMemo(
    () => new Set(excludedIds.map(String)),
    [excludedIds],
  );
  const totalMatches = total ?? contacts.length;
  const matchCountIsLowerBound = pageInfo?.hasNextPage === true;
  const selectedCount = filterModeActive
    ? Math.max(0, totalMatches - excludedIds.length)
    : selectedIds.length;
  const selectedCountIsLowerBound = filterModeActive && matchCountIsLowerBound;
  const visibleSelectedCount = contacts.filter((contact) =>
    isSelected(contact.id, filterModeActive, selectedIdSet, excludedIdSet),
  ).length;
  const hiddenExplicitCount = !filterModeActive
    ? Math.max(0, selectedIds.length - visibleSelectedCount)
    : 0;
  const hasNextPage =
    pageInfo?.hasNextPage ?? page * CONTACTS_PER_PAGE < totalMatches;
  const selectionFilter = useMemo(
    () => ({ all: true, ...searchFilter }),
    [searchFilter],
  );

  useEffect(() => {
    setSearch("");
    setPage(1);
    setMode("explicit");
    setSelectedIds(initialKey ? initialKey.split("\n") : []);
    setExcludedIds([]);
    setPastedContactIds("");
  }, [initialKey, resetKey]);

  useEffect(() => {
    setPage(1);
    if (filterModeActive) {
      setExcludedIds([]);
    }
  }, [effectiveListFilter, filterModeActive]);

  useEffect(() => {
    if (filterModeActive) {
      onChange(
        {
          mode: "filter",
          resource: "contacts",
          filter: selectionFilter,
          sort: { field: "last_seen", order: "DESC" },
          excluded_contact_ids: excludedIds,
        },
        {
          mode: "filter",
          count: selectedCount,
          total: totalMatches,
          selectedAllMatching: true,
        },
      );
      return;
    }

    onChange(
      {
        mode: "explicit",
        contact_ids: selectedIds,
      },
      {
        mode: "explicit",
        count: selectedCount,
        total: totalMatches,
        selectedAllMatching: false,
      },
    );
  }, [
    excludedIds,
    filterModeActive,
    onChange,
    selectionFilter,
    selectedCount,
    selectedIds,
    totalMatches,
  ]);

  const toggleContact = useCallback(
    (contactId: Identifier) => {
      if (disabled) return;
      if (filterModeActive) {
        setExcludedIds((current) =>
          hasIdentifier(current, contactId)
            ? withoutIdentifier(current, contactId)
            : uniqueIdentifiers([...current, contactId]),
        );
        return;
      }

      setSelectedIds((current) =>
        hasIdentifier(current, contactId)
          ? withoutIdentifier(current, contactId)
          : uniqueIdentifiers([...current, contactId]),
      );
    },
    [disabled, filterModeActive],
  );

  const selectPage = () => {
    setMode("explicit");
    setExcludedIds([]);
    setSelectedIds((current) =>
      uniqueIdentifiers([...current, ...contacts.map((contact) => contact.id)]),
    );
  };

  const selectAllMatching = () => {
    setMode("filter");
    setExcludedIds([]);
  };

  const clearSelection = () => {
    if (selectAllMatchingByDefault) {
      setExcludedIds([]);
      return;
    }
    setMode("explicit");
    setSelectedIds([]);
    setExcludedIds([]);
  };

  const addPastedIds = () => {
    const ids = parseContactIds(pastedContactIds);
    if (!ids.length) return;
    setMode("explicit");
    setExcludedIds([]);
    setSelectedIds((current) => uniqueIdentifiers([...current, ...ids]));
    setPastedContactIds("");
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1 space-y-2">
          <Label htmlFor="broadcast-contact-search">
            {translate("crm.whatsapp.broadcasts.recipient_search")}
          </Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
            <Input
              id="broadcast-contact-search"
              value={search}
              disabled={disabled}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-8"
              placeholder={translate(
                "crm.whatsapp.broadcasts.recipient_search_placeholder",
              )}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={
              disabled || selectAllMatchingByDefault || contacts.length === 0
            }
            onClick={selectPage}
          >
            <ListChecks className="size-4" />
            {translate("crm.whatsapp.broadcasts.select_page")}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || filterModeActive || totalMatches === 0}
            onClick={selectAllMatching}
          >
            <Users className="size-4" />
            {translate("crm.whatsapp.broadcasts.select_all_matching")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={
              disabled ||
              (selectAllMatchingByDefault
                ? excludedIds.length === 0
                : selectedCount === 0)
            }
            onClick={clearSelection}
          >
            <X className="size-4" />
            {translate("crm.whatsapp.broadcasts.clear_selection")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge variant="outline">
          {translate(
            matchCountIsLowerBound
              ? "crm.whatsapp.broadcasts.matching_contacts_more"
              : "crm.whatsapp.broadcasts.matching_contacts",
            matchCountIsLowerBound
              ? { count: totalMatches }
              : { smart_count: totalMatches },
          )}
        </Badge>
        <Badge variant={filterModeActive ? "default" : "secondary"}>
          {translate(
            filterModeActive
              ? selectedCountIsLowerBound
                ? "crm.whatsapp.broadcasts.all_matching_selected_more"
                : "crm.whatsapp.broadcasts.all_matching_selected"
              : "crm.whatsapp.broadcasts.explicit_selected",
            selectedCountIsLowerBound
              ? { count: selectedCount }
              : { smart_count: selectedCount },
          )}
        </Badge>
        {excludedCount > 0 && (
          <Badge variant="destructive">
            {translate("crm.whatsapp.broadcasts.matching_contacts_excluded", {
              smart_count: excludedCount,
            })}
          </Badge>
        )}
        {hiddenExplicitCount > 0 && (
          <span className="text-muted-foreground">
            {translate("crm.whatsapp.broadcasts.hidden_selected", {
              smart_count: hiddenExplicitCount,
            })}
          </span>
        )}
      </div>

      <div className="max-h-80 overflow-auto rounded-md border">
        {isPending ? (
          <div className="p-4 text-sm text-muted-foreground">
            {translate("ra.message.loading")}
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">
            {translate("crm.whatsapp.broadcasts.no_contacts")}
          </div>
        ) : (
          <div className="divide-y">
            {contacts.map((contact) => {
              const selected = isSelected(
                contact.id,
                filterModeActive,
                selectedIdSet,
                excludedIdSet,
              );
              return (
                <ContactAudienceRow
                  key={String(contact.id)}
                  contact={contact}
                  selected={selected}
                  disabled={disabled}
                  onToggle={() => toggleContact(contact.id)}
                />
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          aria-label={translate("crm.whatsapp.broadcasts.previous_page")}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="text-xs text-muted-foreground">
          {translate("crm.whatsapp.broadcasts.page_count", { page })}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled || !hasNextPage}
          onClick={() => setPage((current) => current + 1)}
          aria-label={translate("crm.whatsapp.broadcasts.next_page")}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>

      <details className="rounded-md border px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium">
          {translate("crm.whatsapp.broadcasts.advanced_contact_ids")}
        </summary>
        <div className="mt-3 grid gap-2">
          <Textarea
            value={pastedContactIds}
            disabled={disabled || selectAllMatchingByDefault}
            onChange={(event) => setPastedContactIds(event.target.value)}
            rows={3}
            placeholder={translate("crm.whatsapp.broadcasts.paste_contact_ids")}
          />
          <div>
            <Button
              type="button"
              variant="outline"
              disabled={
                disabled ||
                selectAllMatchingByDefault ||
                parseContactIds(pastedContactIds).length === 0
              }
              onClick={addPastedIds}
            >
              {translate("crm.whatsapp.broadcasts.add_pasted_ids")}
            </Button>
          </div>
        </div>
      </details>
    </div>
  );
}

function ContactAudienceRow({
  contact,
  selected,
  disabled,
  onToggle,
}: {
  contact: Contact;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const translate = useTranslate();
  const eligibility = contactEligibility(contact);

  return (
    <div
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected && "bg-muted/50",
        disabled && "cursor-not-allowed opacity-60",
      )}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <Checkbox
        checked={selected}
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none"
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">
          {contactLabel(contact)}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {contactSubtitle(contact)}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={eligibility.variant}>
          {translate(eligibility.label)}
        </Badge>
        {selected && <Check className="size-4 text-primary" />}
      </div>
    </div>
  );
}

function isSelected(
  contactId: Identifier,
  filterModeActive: boolean,
  selectedIdSet: Set<string>,
  excludedIdSet: Set<string>,
) {
  return filterModeActive
    ? !excludedIdSet.has(String(contactId))
    : selectedIdSet.has(String(contactId));
}

function contactLabel(contact: Contact) {
  const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
  return name || String(contact.id);
}

function contactSubtitle(contact: Contact) {
  return (
    contact.whatsapp_user_id ||
    contact.company_name ||
    contact.title ||
    String(contact.id)
  );
}

function contactEligibility(contact: Contact): {
  label: string;
  variant: "default" | "secondary" | "destructive";
} {
  if (
    contact.whatsapp_opt_in_status === "opted_out" ||
    contact.whatsapp_opted_out_at
  ) {
    return {
      label: "crm.whatsapp.broadcasts.opted_out",
      variant: "destructive",
    };
  }
  if (!contact.whatsapp_user_id) {
    return {
      label: "crm.whatsapp.broadcasts.missing_whatsapp_id",
      variant: "secondary",
    };
  }
  if (contact.whatsapp_opt_in_status === "unknown") {
    return {
      label: "crm.whatsapp.broadcasts.eligibility_unknown",
      variant: "secondary",
    };
  }
  return {
    label: "crm.whatsapp.broadcasts.whatsapp_ready",
    variant: "default",
  };
}

function parseContactIds(value: string): Identifier[] {
  return uniqueIdentifiers(
    value
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean),
  );
}

function uniqueIdentifiers(values: Identifier[]) {
  const seen = new Set<string>();
  const result: Identifier[] = [];
  for (const value of values) {
    const key = String(value).trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
}

function hasIdentifier(values: Identifier[], id: Identifier) {
  return values.some((value) => String(value) === String(id));
}

function withoutIdentifier(values: Identifier[], id: Identifier) {
  return values.filter((value) => String(value) !== String(id));
}
